import { useMemo, useState } from "react";
import { optionColor } from "./NotionPropertyEditor";
import { fmtFull } from "../../utils";
import { clicable } from "../ClicableTeclado.jsx";
import { YaEnMisTareas } from "./NotionOrigen";

// ── Vista de tabla de una database de Notion ─────────────────────────────────
// Pinta CUALQUIER propiedad de la página a partir del esquema real de la
// database y del valor plano que guarda la sincronización en `properties`.
// Es de solo lectura: para editar se abre el panel de detalle pulsando la fila.

const isEmoji = (str) => str && /\p{Emoji}/u.test(str) && str.length <= 4;

// El título ya tiene su propia columna fija
const HIDDEN_TYPES = new Set(["title"]);

// Columnas sugeridas cuando el usuario todavía no ha elegido ninguna
export function defaultColumns(schema = []) {
  const pref = ["status", "select", "multi_select", "people", "date", "checkbox", "number"];
  return schema
    .filter(p => !HIDDEN_TYPES.has(p.type))
    .slice()
    .sort((a, b) => {
      const ia = pref.indexOf(a.type), ib = pref.indexOf(b.type);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    })
    .slice(0, 5)
    .map(p => p.name);
}

const chip = (bg, text, key) => (
  <span key={key} style={{
    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
    background: bg, color: "var(--text2)", whiteSpace: "nowrap",
  }}>{text}</span>
);

const empty = <span style={{ color: "var(--text3)" }}>—</span>;

// Fecha ISO (con o sin hora, con o sin fin) → texto corto
function dateText(v) {
  if (!v) return null;
  const start = typeof v === "string" ? v : v.start;
  if (!start) return null;
  const end = typeof v === "string" ? null : v.end;
  return end ? `${fmtFull(start)} → ${fmtFull(end)}` : fmtFull(start);
}

// Valor de una propiedad, renderizado según su tipo
export function PropertyValue({ def, entry, relationTitles = {} }) {
  const type  = entry?.type || def?.type;
  const value = entry?.value;

  if (value === null || value === undefined || value === "" ||
      (Array.isArray(value) && value.length === 0)) return empty;

  const colorOf = (name) => optionColor(def?.options?.find(o => o.name === name)?.color);

  switch (type) {
    case "status":
    case "select":
      return chip(colorOf(value), value);

    case "multi_select":
      return <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
        {value.map((v, i) => chip(colorOf(v), v, i))}
      </span>;

    case "people":
      return <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
        {value.map((p, i) => chip("var(--border)", p.name || p.id, i))}
      </span>;

    case "relation":
      return <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
        {value.map((id, i) => chip("#e0e7ff", relationTitles[id] || "Página vinculada", i))}
      </span>;

    case "checkbox":
      return <span style={{ color: value ? "#10b981" : "var(--text3)", fontWeight: 700 }}>{value ? "✓" : "—"}</span>;

    case "date":
      return dateText(value) ?? empty;

    case "number":
      return <span style={{ fontVariantNumeric: "tabular-nums" }}>{value}</span>;

    case "url":
      return <a href={value} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
        style={{ color: "var(--accent)", textDecoration: "none" }}>{value}</a>;

    case "email":
      return <a href={`mailto:${value}`} onClick={e => e.stopPropagation()}
        style={{ color: "var(--accent)", textDecoration: "none" }}>{value}</a>;

    case "files":
      return <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
        {value.map((f, i) => f.url
          ? <a key={i} href={f.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
              style={{ color: "var(--accent)", fontSize: 11.5 }}>{f.name || "archivo"}</a>
          : chip("var(--border)", f.name || "archivo", i))}
      </span>;

    case "created_time":
    case "last_edited_time":
      return dateText(value) ?? String(value);

    default: {
      // formula, rollup, unique_id, rich_text, phone_number, created_by...
      if (typeof value === "boolean") return value ? "✓" : "—";
      if (typeof value === "object")  return dateText(value) ?? JSON.stringify(value);
      return String(value);
    }
  }
}

// Clave comparable para ordenar por una columna
function sortKey(entry) {
  const v = entry?.value;
  if (v === null || v === undefined) return null;
  if (Array.isArray(v)) return v.length ? v.map(x => (typeof x === "object" ? x.name || x.id || "" : x)).join(", ").toLowerCase() : null;
  if (typeof v === "object")  return v.start || null;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "number")  return v;
  return String(v).toLowerCase();
}

// `seleccion` (modo «Seleccionar» de Mis tareas): { ids: Set de elegidas, ya: Set de las que ya estan,
// onToggle(page), onQuitar(page) }. Las que ya estan no tienen casilla: llevan la marca y «Quitar».
export default function NotionTable({ pages, schema, visibleCols, relationTitles, onRowClick, enlace = null, seleccion = null }) {
  const [sort, setSort] = useState({ col: null, dir: "asc" });   // col null = orden de Notion

  const cols = useMemo(
    () => visibleCols.map(name => schema.find(p => p.name === name)).filter(Boolean),
    [visibleCols, schema]
  );

  const rows = useMemo(() => {
    if (!sort.col) return pages;
    const mul = sort.dir === "asc" ? 1 : -1;
    return pages.slice().sort((a, b) => {
      const ka = sort.col === "__title" ? (a.title || "").toLowerCase() : sortKey(a.properties?.[sort.col]);
      const kb = sort.col === "__title" ? (b.title || "").toLowerCase() : sortKey(b.properties?.[sort.col]);
      if (ka === null || ka === undefined) return kb === null || kb === undefined ? 0 : 1;   // vacíos al final
      if (kb === null || kb === undefined) return -1;
      if (ka === kb) return 0;
      return ka > kb ? mul : -mul;
    });
  }, [pages, sort]);

  const toggleSort = (col) => setSort(s =>
    s.col !== col       ? { col, dir: "asc" }
    : s.dir === "asc"   ? { col, dir: "desc" }
    :                     { col: null, dir: "asc" }
  );

  const thS = {
    position: "sticky", top: 0, zIndex: 1, background: "var(--surface)",
    textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--text3)",
    textTransform: "uppercase", letterSpacing: "0.04em", padding: "8px 12px",
    borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", cursor: "pointer",
    userSelect: "none",
  };
  const tdS = {
    padding: "9px 12px", fontSize: 12.5, color: "var(--text)",
    borderBottom: "1px solid var(--border)", verticalAlign: "top",
  };

  const arrow = (col) => (sort.col !== col ? "" : sort.dir === "asc" ? " ↑" : " ↓");

  return (
    <div style={{ flex: 1, overflow: "auto", border: "1px solid var(--border)", borderRadius: 10, background: "var(--surface)" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 520 }}>
        <thead>
          <tr>
            {enlace && <th style={{ ...thS, width: 46, cursor: "default" }} title="Enlazada en ALM">ALM</th>}
            {seleccion && <th style={{ ...thS, width: 40, cursor: "default" }}><span className="sr-only">Elegir</span></th>}
            <th style={{ ...thS, minWidth: 220 }} onClick={() => toggleSort("__title")}>Título{arrow("__title")}</th>
            {cols.map(def => (
              <th key={def.name} style={thS} title={def.type} onClick={() => toggleSort(def.name)}>
                {def.name}{arrow(def.name)}
              </th>
            ))}
            {seleccion && <th style={{ ...thS, cursor: "default" }}><span className="sr-only">En Mis tareas</span></th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(page => {
            const id = page.external_id ?? page.id;
            const enlazada = page.enabled !== false;
            return (
              <tr key={id} {...clicable(() => onRowClick?.(page), { role: null, activo: !!onRowClick })}
                style={{ cursor: "pointer", opacity: enlace && !enlazada ? 0.45 : 1 }}
                onMouseEnter={e => { e.currentTarget.style.background = seleccion?.ids.has(id) ? "var(--accent-bg)" : "var(--bg)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = seleccion?.ids.has(id) ? "var(--accent-bg)" : "transparent"; }}>
                {enlace && (
                  <td style={tdS} onClick={e => e.stopPropagation()}>
                    <input type="checkbox" checked={enlazada} disabled={enlace.busy?.includes(id)}
                      onChange={() => enlace.onToggle(page)}
                      title={enlazada ? "Quitar de ALM" : "Traer a ALM"}
                      style={{ accentColor: "var(--accent)", width: 14, height: 14 }} />
                  </td>
                )}
                {seleccion && (
                  <td style={tdS} onClick={e => e.stopPropagation()}>
                    {!seleccion.ya.has(id) && (
                      <input type="checkbox" checked={seleccion.ids.has(id)} onChange={() => seleccion.onToggle(page)}
                        aria-label={`Elegir «${page.title || "Sin título"}»`}
                        style={{ accentColor: "var(--accent)", width: 18, height: 18, cursor: "pointer" }} />
                    )}
                  </td>
                )}
                <td style={{ ...tdS, fontWeight: 600 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                    {isEmoji(page.icon) && <span>{page.icon}</span>}
                    {page.title || "Sin título"}
                  </span>
                </td>
                {cols.map(def => (
                  <td key={def.name} style={tdS}>
                    {page.properties
                      ? <PropertyValue def={def} entry={page.properties[def.name]} relationTitles={relationTitles} />
                      : <span style={{ color: "var(--text3)" }}>—</span>}
                  </td>
                ))}
                {seleccion && (
                  <td style={{ ...tdS, whiteSpace: "nowrap" }} onClick={e => e.stopPropagation()}>
                    {seleccion.ya.has(id) && <YaEnMisTareas page={page} onQuitar={seleccion.onQuitar} />}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
